import type { FunctionComponent } from "react";

import CheckCircleIcon from "@uui/CheckCircle";

import styled from "@mui/material/styles/styled";
import Typography, { TypographyProps } from "@mui/material/Typography";

import Button from "@rui/Button";
import Stack, { Row, StackProps } from "@rui/Stack";

const MostPopularBanner = styled((props) => (
  <Stack variant="center" {...props}>
    <Typography variant="labelMed">Most Popular</Typography>
  </Stack>
))(({ theme }) => ({
  borderRadius: "16px 16px 0 0",
  padding: theme.spacing(1, 1.5),
  textTransform: "uppercase",
  color: theme.palette.chart.teal.dark,
  backgroundColor: "#DEF5F9",
}));

type ContainerProps = StackProps & { isMostPopular: boolean };

export const Container = styled<FunctionComponent<ContainerProps>>(
  ({ isMostPopular, children, ...props }) => (
    <Stack {...props}>
      {isMostPopular && <MostPopularBanner />}
      <Stack paddingTop={isMostPopular ? 0.5 : 1.5} flex={1}>
        {children}
      </Stack>
    </Stack>
  )
)(({ isMostPopular, theme }) => ({
  boxSizing: "border-box",
  border: isMostPopular ? "2px solid" : "1px solid",
  borderColor: isMostPopular
    ? theme.palette.shades.primary[500]
    : theme.palette.shades.neutral[100],
  borderRadius: "16px",
  width: "235px",
  minWidth: "235px",
  height: "fit-content",
  alignSelf: "flex-end",
}));

export const Content = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(1.5, 2, 3),
  minHeight: "270px",
}));

export const Heading = styled(Typography)(({ theme }) => ({
  color: theme.palette.shades.neutral[800],
  fontSize: "1.5rem",
  fontWeight: 500,
  lineHeight: 1,
}));

export const Description = styled(Typography)(({ theme }) => ({
  color: theme.palette.shades.neutral[600],
  fontFamily: theme.typography.fontFamilySecondary,
  fontSize: "0.875rem",
  lineHeight: 18 / 14,
}));

export const Disclaimer = styled(Typography)(({ theme }) => ({
  fontSize: "0.75rem",
  color: theme.palette.shades.neutral[600],
  fontFamily: theme.typography.fontFamilySecondary,
}));

export const UpgradeButton = styled(Button)(({ theme }) => ({
  width: "100%",
  fontSize: "1.125rem",
  "&.Mui-disabled": {
    backgroundColor: theme.palette.shades.neutral[50],
    color: theme.palette.shades.neutral[500],
  },
}));

type PriceProps = TypographyProps & { active?: boolean };

export const Price = styled<FunctionComponent<PriceProps>>(
  ({ active: _a, ...props }) => <Typography {...props} />
)(({ active = true, theme }) => ({
  fontSize: "1.5rem",
  color: active
    ? theme.palette.shades.primary[500]
    : theme.palette.shades.neutral[300],
  textDecoration: active ? "none" : "line-through",
  fontWeight: 500,
}));

export const Footer = styled((props) => (
  <Stack variant="center" {...props}>
    <Typography variant="bodySecondaryMed">View all features</Typography>
  </Stack>
))(({ theme }) => ({
  backgroundColor: theme.palette.shades.ghost[500],
  padding: theme.spacing(1.5, 2),
  borderRadius: "0 0 16px 16px",
  color: theme.palette.shades.primary[500],
  alignSelf: "flex-end",
}));

const FeatureDescription = styled(Typography)(({ theme }) => ({
  fontSize: "0.75rem",
  color: theme.palette.shades.neutral[500],
  fontFamily: theme.typography.fontFamilySecondary,
  paddingLeft: theme.spacing(3),
  lineHeight: 14 / 12,
}));

type FeatureProps = StackProps & { title: string; description?: string };

export const Feature = styled(
  ({ title, description, ...props }: FeatureProps) => (
    <Stack spacing={0.5} {...props}>
      <Row variant="left" spacing={1}>
        <CheckCircleIcon />
        <span>{title}</span>
      </Row>
      {description && <FeatureDescription>{description}</FeatureDescription>}
    </Stack>
  )
)(({ theme }) => ({
  fontSize: "0.875rem",
  color: theme.palette.shades.neutral[800],
  fontFamily: theme.typography.fontFamilySecondary,
  "& .MuiSvgIcon-root": {
    color: theme.palette.shades.meadow[500],
    fontSize: "16px",
  },
}));
